import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';
import BreakingNewsTicker from '../../components/BreakingNewsTicker';
import NewsCard from '../../components/NewsCard';
import Sidebar from '../../components/Sidebar';
import AdPopup from '../../components/AdPopup';
import { subscribeToPublishedArticles } from '../../firebase/firestore';
import { trackVisit } from '../../utils/trackVisit';
import { formatDateTime } from '../../utils/dateHelpers';
import { useLanguage } from '../../context/LanguageContext';
import './Home.css';

const CATEGORIES = ['Politics', 'Sports', 'Technology', 'Entertainment', 'Business', 'World', 'Health', 'Education'];

// Time-range filters — separate from sort direction
const TIME_FILTERS = [
  { key: 'all', label: '📋 All Time' },
  { key: 'today', label: '📅 Today' },
  { key: 'week', label: '🗓 This Week' },
  { key: 'month', label: '📆 This Month' },
];

// "Earliest First" is purely a sort toggle, not a range filter
function isWithin(createdAt, rangeKey) {
  if (rangeKey === 'all') return true;
  const articleDate = createdAt?.toDate ? createdAt.toDate() : new Date(createdAt);
  const now = new Date();
  if (rangeKey === 'today') {
    const start = new Date(now); start.setHours(0, 0, 0, 0);
    return articleDate >= start;
  }
  if (rangeKey === 'week') {
    const start = new Date(now); start.setDate(now.getDate() - 7);
    return articleDate >= start;
  }
  if (rangeKey === 'month') {
    const start = new Date(now); start.setDate(now.getDate() - 30);
    return articleDate >= start;
  }
  return true;
}

export default function Home() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [timeFilter, setTimeFilter] = useState('all');
  const [earliestFirst, setEarliestFirst] = useState(false);
  const { language } = useLanguage();

  useEffect(() => {
    trackVisit({ pageType: 'home', pageURL: '/' });
  }, []);

  useEffect(() => {
    setLoading(true);
    const unsub = subscribeToPublishedArticles((arts) => {
      setArticles(arts);
      setLoading(false);
    }, language);
    return unsub;
  }, [language]); // Re-subscribe whenever language changes

  // Helper: check if article is available in a given language
  const articleHasLanguage = (a, lang) => {
    if (lang === 'all') return true;
    // New format: 'languages' is an array e.g. ['english', 'telugu']
    if (Array.isArray(a.languages)) return a.languages.includes(lang);
    // Old format: 'language' is a comma-separated string e.g. 'english,telugu'
    if (a.language) return a.language.split(',').map(l => l.trim().toLowerCase()).includes(lang);
    // Fallback: treat as english
    return lang === 'english';
  };

  // Helper: get the display title/content in selected language (with English fallback)
  const getLocalizedTitle = (a, lang) => {
    if (lang !== 'all' && a.translations && a.translations[lang] && a.translations[lang].title) {
      return a.translations[lang].title;
    }
    return a.title || '';
  };

  const filteredArticles = useMemo(() => {
    // articles are already language-filtered by the Firestore subscription.
    // We only need to apply the time-range filter and sort here.
    let list = articles;

    // Time-range filter
    list = list.filter(a => isWithin(a.createdAt, timeFilter));

    // Sort: newest first (latest news at top), unless earliestFirst toggled
    list = [...list].sort((a, b) => {
      const aDate = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt);
      const bDate = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt);
      return earliestFirst ? aDate - bDate : bDate - aDate;
    });

    // Attach localized title so cards render the correct language headline
    return list.map(a => ({
      ...a,
      displayTitle: getLocalizedTitle(a, language),
    }));
  }, [articles, language, timeFilter, earliestFirst]);

  const hero = filteredArticles[0];
  const featured = filteredArticles.slice(1, 4);
  const rest = filteredArticles.slice(4);

  const byCategory = (cat) => filteredArticles.filter(a => a.category === cat).slice(0, 4);

  const langLabel = language === 'all' ? 'All Languages' :
    language === 'telugu' ? 'తెలుగు (Telugu)' :
      language === 'hindi' ? 'हिन्दी (Hindi)' : 'English';

  const sectionHeading = () => {
    const range = timeFilter === 'today' ? "Today's" :
      timeFilter === 'week' ? "This Week's" :
        timeFilter === 'month' ? "This Month's" : 'Latest';
    return earliestFirst ? `⏳ ${range} News — Earliest First` : `${range === 'Latest' ? '🗞 ' : ''}${range} News`;
  };

  if (loading) return (
    <>
      <Navbar />
      <BreakingNewsTicker />
      <div className="loading-spinner">Loading latest news...</div>
    </>
  );

  return (
    <>
      <Navbar />
      <BreakingNewsTicker />
      <AdPopup />

      {/* Animated Background Canvas */}
      <div className="page-bg">
        <div className="bg-orb bg-orb-1" />
        <div className="bg-orb bg-orb-2" />
        <div className="bg-orb bg-orb-3" />
        <div className="bg-grid" />
      </div>

      <main className="home-main">

        {/* Language + Time Filter Bar */}
        <div className="filter-bar">
          <div className="container filter-bar-inner">
            <div className="filter-lang-indicator">
              <span className="filter-pill">
                {language === 'all' ? '🌍' : language === 'telugu' ? '🔵' : language === 'hindi' ? '🔷' : '🟢'}
                &nbsp;{langLabel}
              </span>
            </div>
            <div className="time-filter-tabs">
              <span className="filter-label">⏱ Filter by time:</span>
              {TIME_FILTERS.map(tf => (
                <button
                  key={tf.key}
                  className={`time-tab ${timeFilter === tf.key ? 'active' : ''}`}
                  onClick={() => setTimeFilter(tf.key)}
                >
                  {tf.label}
                </button>
              ))}
              {/* Earliest First is a sort toggle, always visible */}
              {/* <button
                className={`time-tab${earliestFirst ? ' active' : ''}`}
                onClick={() => setEarliestFirst(prev => !prev)}
              >
                ⏳ Earliest First
              </button> */}
            </div>
          </div>
        </div>

        {/* No news state */}
        {filteredArticles.length === 0 && (
          <div className="no-news-state">
            <div className="no-news-icon">
              {language === 'telugu' ? '📰' : language === 'hindi' ? '📰' : '📰'}
            </div>
            <h2 className="no-news-title">
              {language === 'telugu' ? 'వార్తలు అందుబాటులో లేవు' :
                language === 'hindi' ? 'कोई समाचार उपलब्ध नहीं है' :
                  'No News Available'}
            </h2>
            <p className="no-news-sub">
              {language === 'all'
                ? 'No articles match the selected time filter.'
                : `No ${langLabel} articles found${timeFilter !== 'all' ? ' for this time period' : ''}. Check back soon!`}
            </p>
            <button className="btn btn-primary" onClick={() => { setTimeFilter('all'); setEarliestFirst(false); }}>
              Clear Filters
            </button>
          </div>
        )}

        {/* HERO */}
        {hero && (
          <section className="hero-section">
            <div className="container">
              <div className="hero-grid">
                <Link to={`/article/${hero.id}`} className="hero-main">
                  <div className="hero-image">
                    {hero.thumbnailURL
                      ? <img src={hero.thumbnailURL} alt={hero.displayTitle || hero.title} />
                      : <div className="hero-placeholder">📰</div>
                    }
                    <div className="hero-overlay">
                      <div className="hero-badges">
                        <span className="badge hero-badge">{hero.category}</span>
                        {(Array.isArray(hero.languages) ? hero.languages : (hero.language || '').split(',')).filter(l => l.trim() && l.trim() !== 'english').map(l => (
                          <span key={l} className={`badge badge-${l.trim()}`}>
                            {l.trim() === 'telugu' ? '🔵 తెలుగు' : '🔷 हिन्दी'}
                          </span>
                        ))}
                      </div>
                      <h1 className="hero-title">{hero.displayTitle || hero.title}</h1>
                      <div className="hero-meta">
                        <span>✍️ {hero.author}</span>
                        <span>{formatDateTime(hero.createdAt)}</span>
                      </div>
                    </div>
                  </div>
                </Link>
                <div className="hero-side">
                  {featured.map(a => (
                    <Link key={a.id} to={`/article/${a.id}`} className="hero-side-card">
                      <div className="hero-side-img">
                        {a.thumbnailURL ? <img src={a.thumbnailURL} alt={a.title} /> : <div className="img-ph">📰</div>}
                      </div>
                      <div className="hero-side-info">
                        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                          <span className="badge" style={{ fontSize: '0.62rem' }}>{a.category}</span>
                          {(Array.isArray(a.languages) ? a.languages : (a.language || '').split(',')).filter(l => l.trim() && l.trim() !== 'english').map(l => (
                            <span key={l} className={`badge badge-${l.trim()}`} style={{ fontSize: '0.62rem' }}>
                              {l.trim() === 'telugu' ? 'తె' : 'हि'}
                            </span>
                          ))}
                        </div>
                        <h3>{a.displayTitle || a.title}</h3>
                        <span className="meta-time">{formatDateTime(a.createdAt)}</span>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </section>
        )}

        {/* MAIN CONTENT */}
        {filteredArticles.length > 0 && (
          <div className="container home-body">
            <div className="home-content-grid">
              <div className="home-main-col">

                {rest.length > 0 && (
                  <section className="home-section">
                    <h2 className="section-title">{sectionHeading()}</h2>
                    <div className="news-grid-3">
                      {rest.map(a => <NewsCard key={a.id} article={a} />)}
                    </div>
                  </section>
                )}

                {CATEGORIES.map(cat => {
                  const catArts = byCategory(cat);
                  if (!catArts.length) return null;
                  return (
                    <section key={cat} className="home-section">
                      <div className="section-title-row">
                        <h2 className="section-title">{cat}</h2>
                        <Link to={`/category/${cat.toLowerCase()}`} className="see-all">See all →</Link>
                      </div>
                      <div className="news-grid-4">
                        {catArts.map(a => <NewsCard key={a.id} article={a} size="small" />)}
                      </div>
                    </section>
                  );
                })}
              </div>
              <div className="home-sidebar-col">
                <Sidebar />
              </div>
            </div>
          </div>
        )}
      </main>
      <Footer />
    </>
  );
}